import { getDocumentsByIds } from "@/services/opsmate/documents";
import {
  getReferenceRelatedDocIds,
  getDocInfoDocumentGuid,
  getDocInfoDataSource,
  getDocInfoCategoryIds,
  getDocInfoSourcePathAskId,
  isDocInfoValid,
  matchDocInfoByRelatedIds,
} from "@/utils/conversation";
import { formatFileSize } from "@/utils/formatters";
import { splitReferencesFence } from "@/utils/referencesFence";
import { filterDisplayProgressSteps } from "@/utils/progressSteps";

type RowDoc = {
  name: string;
  citations: string;
  canDownload: boolean;
  id?: string;
  segmentNums: number[];
  relatedIds: string[];
};

export interface BuildSessionLogItemsResult {
  items: any[];
  hasDocuments: boolean;
}

export async function buildSessionLogItems(rawList: any[]): Promise<BuildSessionLogItemsResult> {
  const refDocIdSet = new Set<string>();
  const messageRecords: Array<{
    baseKey: string;
    role: "user" | "ai";
    content: string;
    execMsg?: any;
    summary?: string;
    docMap?: Map<string, RowDoc>;
    references?: any[];
    rationale?: string;
    progress?: any[];
    feedback?: "like" | "dislike" | "default";
    translatedSummary?: string;
    translatedAnswer?: string;
    translatedRationale?: string;
  }> = [];

  rawList.forEach((row: any, index: number) => {
    const entity = row?.Entity ?? row ?? {};
    const qType = String(
      entity.SessionLog_QuestionType ??
        entity.sessionLog_QuestionType ??
        entity.sessionlog_questiontype ??
        entity.QuestionType ??
        "",
    );
    const baseKey = String(entity.ID ?? entity.Id ?? entity.id ?? entity.SessionLog_ID ?? index);

    if (qType === "1") {
      const questionText =
        entity.SessionLog_Question ??
        entity.sessionLog_Question ??
        entity.SessionLog_question ??
        entity.Question ??
        entity.question ??
        "";
      if (questionText) {
        messageRecords.push({ baseKey, role: "user", content: String(questionText) });
      }
      return;
    }

    if (qType !== "0") return;

    const rawAnswerText =
      entity.SessionLog_Answer ??
      entity.sessionLog_Answer ??
      entity.SessionLog_answer ??
      entity.Answer ??
      entity.answer ??
      "";
    const answerText = splitReferencesFence(String(rawAnswerText ?? "")).text;
    const rationaleText = String(
      entity.SessionLog_Rationale ??
        entity.sessionLog_Rationale ??
        entity.SessionLog_rationale ??
        entity.Rationale ??
        entity.rationale ??
        "",
    );

    const praiseValue = Number(
      entity.SessionLog_UserIsPraiseValue ??
        entity.sessionLog_UserIsPraiseValue ??
        entity.sessionlog_userispraisevalue ??
        entity.UserIsPraise ??
        entity.userispraise ??
        0,
    );
    const feedback: "like" | "dislike" | "default" =
      praiseValue === 1 ? "like" : praiseValue === -1 ? "dislike" : "default";

    let references: any[] = [];
    const rawRefs = entity.SessionLog_references ?? entity.sessionlog_references;
    if (Array.isArray(rawRefs)) {
      references = rawRefs;
    } else {
      const quoteRaw =
        entity.SessionLog_Quote ??
        entity.sessionLog_Quote ??
        entity.SessionLog_quote ??
        entity.Quote ??
        "";
      if (typeof quoteRaw === "string" && quoteRaw.trim()) {
        try {
          const parsed = JSON.parse(quoteRaw);
          if (Array.isArray(parsed)) references = parsed;
        } catch {
          /* ignore */
        }
      }
    }

    const docMap = new Map<string, RowDoc>();
    references.forEach((ref: any, idx: number) => {
      const segmentNum = idx + 1;
      const docId = ref?.references_document_id ?? ref?.document_id ?? "";
      const relatedIds = getReferenceRelatedDocIds(ref);
      relatedIds.forEach((rid) => refDocIdSet.add(rid));
      const key = docId || "unknown";
      const rawSegment = (ref?.references_segment ?? "").toString();
      const marker = "Content: ";
      const markerIndex = rawSegment.indexOf(marker);
      const seg = markerIndex >= 0 ? rawSegment.slice(markerIndex + marker.length) : rawSegment;
      const pos = ref?.references_segment_position ?? ref?.segment_position ?? "";
      const line = pos ? `Page ${pos}: ${seg}` : seg;
      const existed = docMap.get(key);

      if (!existed) {
        docMap.set(key, {
          id: docId,
          name: docId || "未知文档",
          citations: line,
          canDownload: true,
          segmentNums: [segmentNum],
          relatedIds,
        });
      } else {
        existed.segmentNums.push(segmentNum);
        relatedIds.forEach((rid) => {
          if (!existed.relatedIds.includes(rid)) existed.relatedIds.push(rid);
        });
        if (line) existed.citations = `${existed.citations}\n\n---\n\n${line}`;
        docMap.set(key, existed);
      }
    });

    let progressSteps: any[] = [];
    const rawProgress =
      entity.SessionLog_ProgressLog ??
      entity.sessionLog_ProgressLog ??
      entity.SessionLog_progresslog ??
      entity.ProgressLog;
    if (Array.isArray(rawProgress)) {
      progressSteps = rawProgress;
    } else if (typeof rawProgress === "string" && rawProgress.trim()) {
      try {
        const parsed = JSON.parse(rawProgress);
        if (Array.isArray(parsed)) progressSteps = parsed;
      } catch {
        /* ignore */
      }
    }

    let execMsg: Record<string, unknown>;
    try {
      execMsg = JSON.parse(
        entity?.SessionLog_ExecutionMessage ??
          entity?.sessionLog_ExecutionMessage ??
          entity?.ExecutionMessage ??
          "{}",
      );
    } catch {
      execMsg = {};
    }

    messageRecords.push({
      baseKey,
      role: "ai",
      content: String(answerText),
      execMsg,
      summary: String(
        entity.SessionLog_Summary ??
          entity.sessionLog_Summary ??
          entity.SessionLog_summary ??
          entity.Summary ??
          "",
      ),
      docMap,
      references,
      rationale: rationaleText,
      // 与会话区实时流式展示口径一致：丢弃进度提示、仅保留带命中文档名的检索结果 trace
      progress: filterDisplayProgressSteps(progressSteps),
      feedback,
      translatedSummary: String(
        entity.SessionLog_TranslatedSummary ??
          entity.sessionLog_TranslatedSummary ??
          entity.SessionLog_translatedsummary ??
          "",
      ),
      translatedAnswer: String(
        entity.SessionLog_TranslatedAnswer ??
          entity.sessionLog_TranslatedAnswer ??
          entity.SessionLog_translatedanswer ??
          "",
      ),
      translatedRationale: String(
        entity.SessionLog_TranslatedRationale ??
          entity.sessionLog_TranslatedRationale ??
          entity.SessionLog_translatedrationale ??
          "",
      ),
    });
  });

  let docInfoList: any[] = [];
  if (refDocIdSet.size > 0) {
    try {
      const docInfoResp: any = await getDocumentsByIds(Array.from(refDocIdSet));
      docInfoList =
        (Array.isArray(docInfoResp?.data) && docInfoResp.data) ||
        (Array.isArray(docInfoResp?.Data) && docInfoResp.Data) ||
        (Array.isArray(docInfoResp) && docInfoResp) ||
        [];
    } catch (e) {
      console.error("getDocumentsByIds (session logs) failed", e);
    }
  }

  const items: any[] = [];
  for (const rec of messageRecords) {
    const documents = rec.docMap
      ? Array.from(rec.docMap.entries()).map(([, doc]) => {
          const matchedInfo = matchDocInfoByRelatedIds(docInfoList, doc.relatedIds, doc.name);
          if (!matchedInfo) {
            return {
              id: doc.id,
              name: doc.name,
              citations: doc.citations,
              canDownload: doc.canDownload,
              segmentNums: doc.segmentNums,
            };
          }

          const rawSize = matchedInfo.FileSize ?? matchedInfo.fileSize ?? matchedInfo.size;
          const uploadedAt =
            matchedInfo.CreateTime ??
            matchedInfo.FileCreateTime ??
            matchedInfo.FileUpdateTime ??
            matchedInfo.uploadTime ??
            matchedInfo.uploadedAt ??
            matchedInfo.createTime;
          const displayName =
            matchedInfo.FileName ??
            matchedInfo.fileName ??
            matchedInfo.documentName ??
            matchedInfo.DocumentName ??
            doc.name;

          return {
            id: doc.id,
            guidName: getDocInfoDocumentGuid(matchedInfo) || undefined,
            sourcePathAskId: getDocInfoSourcePathAskId(matchedInfo) || undefined,
            docCategoryIds: getDocInfoCategoryIds(matchedInfo),
            name: displayName ? String(displayName) : doc.name,
            citations: doc.citations,
            canDownload: doc.canDownload,
            segmentNums: doc.segmentNums,
            size: formatFileSize(rawSize) || undefined,
            uploadedAt: uploadedAt ? String(uploadedAt) : undefined,
            fileUrl: matchedInfo.DownloadURL ? String(matchedInfo.DownloadURL) : undefined,
            dataSource: getDocInfoDataSource(matchedInfo) || undefined,
            inDocumentList: isDocInfoValid(matchedInfo),
          };
        })
      : [];

    if (rec.role === "user") {
      items.push({ key: `${rec.baseKey}-q`, role: "user", content: rec.content });
    } else {
      items.push({
        key: `${rec.baseKey}-a`,
        role: "ai",
        status: "success",
        content: rec.content,
        summary: rec.summary ?? "",
        rationale: rec.rationale ?? "",
        execMsg: rec.execMsg ?? {},
        documents,
        references: rec.references ?? [],
        progress: Array.isArray(rec.progress) ? rec.progress : [],
        extraInfo: {
          feedback: rec.feedback ?? "default",
          sessionLogId: rec.baseKey,
          ...(rec.translatedSummary || rec.translatedAnswer || rec.translatedRationale
            ? {
                isTranslated: false,
                ...(rec.translatedSummary ? { translatedSummary: rec.translatedSummary } : {}),
                ...(rec.translatedAnswer ? { translatedText: rec.translatedAnswer } : {}),
                ...(rec.translatedRationale
                  ? { translatedRationale: rec.translatedRationale }
                  : {}),
              }
            : {}),
        },
      });
    }
  }

  return {
    items,
    hasDocuments: items.some((m: any) => Array.isArray(m.documents) && m.documents.length > 0),
  };
}
